import React from 'react'
import { LockSimple, Eye } from '@phosphor-icons/react'

/**
 * Shared access affordances for permission-gated Settings sections.
 *
 * Both are UX only. The boundary is the backend: `GET /auth/config` strips
 * the integration/AI blocks entirely for roles without the matching cell
 * (`_redact_config_for_role`), so a locked section has no values to leak even
 * if someone renders it by hand or reads the response in devtools.
 */

/** Full-panel replacement for a role with no access to this section at all. */
export const SectionLocked: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="h-11 w-11 rounded-lg bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
      <LockSimple className="w-5 h-5 text-muted-foreground dark:text-[#8a8f98]" />
    </div>
    <h2
      className="text-foreground dark:text-[#f7f8f8]"
      style={{ fontSize: '18px', fontWeight: 590, letterSpacing: '-0.2px' }}
    >
      {title}
    </h2>
    <p
      className="text-muted-foreground dark:text-[#8a8f98] mt-2 max-w-md"
      style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
    >
      {children}
    </p>
  </div>
)

/** Banner for a role that may look but not change (Manager's "view" level). */
export const ReadOnlyBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-muted/40 px-3.5 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]">
    <Eye className="w-4 h-4 mt-px shrink-0 text-muted-foreground dark:text-[#8a8f98]" />
    <p
      className="text-muted-foreground dark:text-[#8a8f98]"
      style={{ fontSize: '12.5px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
    >
      {children}
    </p>
  </div>
)
